import {
  AppShell,
  Button,
  Group,
  Loader,
  NumberInput,
  Switch,
  TextInput,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { reset, initialize, updateField } from "../slices/form/userTab";

import { useEffect } from "react";
import {
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { IsBlockNav, SetBlockNav } from "../App";
import { AdminTabHeader } from "../shared/components/TabHeader";
import {
  SaveUserRequest,
  useGetUserAttributesQuery,
  useSaveUserMutation,
} from "../slices/api/usersApi";

interface Props {
  userId: string; // User's UID from authentication
  setTabDescription: (name: string) => void;
}

const UserTab = (props: Props) => {
  const { data: userAttributes, hasChanges } = useAppSelector(
    (state: RootState) => state.userTab
  );
  const {
    data: userAttributesResponse,
    isFetching: isFetchingUserAttributesResponse,
    isError: isErrorFetchingUserAttributesResponse,
    error: userAttributesError,
  } = useGetUserAttributesQuery({ userId: props.userId });

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (userAttributesResponse) {
      dispatch(initialize(userAttributesResponse));
      props.setTabDescription(userAttributesResponse.email);
    }
  }, [userAttributesResponse]);
  useEffect(() => {
    // During development useEffect is called twice by React.
    // We need to protect against that by returning if hasChanges didn't change
    if (IsBlockNav() === hasChanges) return;
    if (hasChanges) {
      SetBlockNav(true);
    } else {
      // Can happen if we support undo
      SetBlockNav(false);
    }
  }, [hasChanges]);

  const [saveUserMutation] = useSaveUserMutation();

  const handleSave = async () => {
    if (userAttributes == null || userAttributesResponse == null) {
      console.error("undefined");
      return;
    }

    const request: SaveUserRequest = {
      userId: props.userId,
      updateTime: userAttributesResponse.updateTime,
      firstName: userAttributes.firstName,
      lastName: userAttributes.lastName,
      canOwnVents: userAttributes.canOwnVents,
      emailAllotment: userAttributes.emailAllotment,
      ventLimit: userAttributes.ventLimit,
      guestLimit: userAttributes.guestLimit,
      isAdmin: userAttributes.isAdmin,
      isDisabled: userAttributes.isDisabled,
      canTestTextChannel: userAttributes.canTestTextChannel,
      isOptedOutTextChannel: userAttributes.isOptedOutTextChannel,
    };

    try {
      await saveUserMutation(request).unwrap();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (isErrorFetchingUserAttributesResponse) {
    handleFetchingError(userAttributesError);
    return null;
  }

  if (
    isFetchingUserAttributesResponse ||
    !userAttributesResponse ||
    !userAttributes
  ) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }
  return (
    <>
      <AppShell.Main>
        <AdminTabHeader label={`User: ${userAttributesResponse.email}`} />
        <Group>
          <TextInput
            style={{ width: "150px" }}
            label="First Name"
            required
            value={userAttributes.firstName ?? ""}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "firstName",
                  value: event.currentTarget.value,
                })
              )
            }
          />

          <TextInput
            style={{ width: "150px" }}
            label="Last Name"
            value={userAttributes.lastName ?? ""}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "lastName",
                  value: event.currentTarget.value,
                })
              )
            }
          />
        </Group>
        <Switch
          label="Is Disabled"
          mt="md"
          checked={userAttributes.isDisabled}
          onChange={(event) =>
            dispatch(
              updateField({
                field: "isDisabled",
                value: event.currentTarget.checked,
              })
            )
          }
        />
        <Switch
          label="Is Admin"
          mt="md"
          checked={userAttributes.isAdmin}
          onChange={(event) =>
            dispatch(
              updateField({
                field: "isAdmin",
                value: event.currentTarget.checked,
              })
            )
          }
        />

        <Switch
          label="Can Own Events"
          mt="md"
          checked={userAttributes.canOwnVents}
          onChange={(event) =>
            dispatch(
              updateField({
                field: "canOwnVents",
                value: event.currentTarget.checked,
              })
            )
          }
        />

        <Switch
          label="Opted Out of Text Channel"
          description="This will set the flag in the database, but doesn't change the opt out as known to the SMS provider."
          mt="md"
          checked={userAttributes.isOptedOutTextChannel}
          onChange={(event) =>
            dispatch(
              updateField({
                field: "isOptedOutTextChannel",
                value: event.currentTarget.checked,
              })
            )
          }
        />

        <Switch
          label="Can Test Text Channel"
          mt="md"
          checked={userAttributes.canTestTextChannel}
          onChange={(event) =>
            dispatch(
              updateField({
                field: "canTestTextChannel",
                value: event.currentTarget.checked,
              })
            )
          }
        />

        <NumberInput
          style={{ width: "200px" }}
          min={0}
          maxLength={6}
          mt="md"
          allowNegative={false}
          allowDecimal={false}
          label="Guest Limit Per Event"
          value={userAttributes.guestLimit}
          onChange={(value) => {
            dispatch(
              updateField({
                field: "guestLimit",
                value: value === "" ? 0 : value,
              })
            );
          }}
        />

        <NumberInput
          style={{ width: "200px" }}
          min={0}
          maxLength={6}
          mt="md"
          allowNegative={false}
          allowDecimal={false}
          label="Active Event Limit"
          value={userAttributes.ventLimit}
          onChange={(value) => {
            dispatch(
              updateField({
                field: "ventLimit",
                value: value === "" ? 0 : value,
              })
            );
          }}
        />

        <NumberInput
          style={{ width: "200px" }}
          mt="md"
          allowNegative={false}
          allowDecimal={false}
          min={0}
          maxLength={6}
          label="Email Allotment Remaining"
          value={userAttributes.emailAllotment}
          onChange={(value) => {
            dispatch(
              updateField({
                field: "emailAllotment",
                value: value === "" ? 0 : value,
              })
            );
          }}
        />

        <div>
          <Button
            mt="xs"
            mr="md"
            onClick={() => {
              dispatch(reset());
            }}
            disabled={!hasChanges}
          >
            Discard Changes
          </Button>
          <Button mt="xs" onClick={handleSave} disabled={!hasChanges}>
            Save User
          </Button>
        </div>
      </AppShell.Main>
    </>
  );
};

export default UserTab;
